import { useState, useEffect } from "react";
import api, { apiErr } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { fmtEuro } from "../../lib/format";
import { toast } from "sonner";
import { Plus, Copy, Check } from "lucide-react";

export const Canali = () => {
  const [canali, setCanali] = useState([]);
  const [riepilogo, setRiepilogo] = useState([]);
  const [nome, setNome] = useState("");
  const [slug, setSlug] = useState("");
  const [copied, setCopied] = useState("");

  const load = () => api.get("/admin/canali").then((r) => setCanali(r.data)).catch((e) => toast.error(apiErr(e)));
  const loadRiepilogo = () => api.get("/admin/canali/riepilogo").then((r) => setRiepilogo(r.data)).catch(() => {});
  useEffect(() => { load(); loadRiepilogo(); }, []);

  const origin = window.location.origin;
  const linkFor = (s) => (s === "diretto" ? `${origin}/` : `${origin}/c/${s}`);

  const create = async () => {
    if (!nome.trim() || !slug.trim()) { toast.error("Inserisci nome e slug."); return; }
    try {
      await api.post("/admin/canali", { nome, slug, attivo: true });
      setNome(""); setSlug("");
      toast.success("Canale creato.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggle = async (c) => {
    try { await api.put(`/admin/canali/${c.id}`, { attivo: !c.attivo }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const copy = (s) => {
    navigator.clipboard.writeText(linkFor(s));
    setCopied(s);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div data-testid="admin-channels-manager">
      <h1 className="text-2xl font-semibold text-[#2B3A42] mb-6">Canali</h1>

      <div className="bg-white rounded-2xl border border-[#DDE5E2] p-5 mb-6">
        <h3 className="text-sm uppercase tracking-[0.12em] font-semibold text-[#6C8EAD] mb-4">Nuovo canale</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label className="text-sm">Nome</Label>
            <Input data-testid="canale-nome-input" className="h-10 border-[#DDE5E2]" value={nome}
              onChange={(e) => setNome(e.target.value)} placeholder="Es. Instagram" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Slug</Label>
            <Input data-testid="canale-slug-input" className="h-10 border-[#DDE5E2]" value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, ""))} placeholder="instagram" />
          </div>
          <Button data-testid="canale-create-btn" onClick={create} className="h-10 gap-2 rounded-full bg-[#6C8EAD] hover:bg-[#4F708C] text-white">
            <Plus className="h-4 w-4" /> Crea canale
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#DDE5E2] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#EDF2F0] text-[#2B3A42] text-left">
                <th className="px-4 py-3 font-medium">Nome</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Link</th>
                <th className="px-4 py-3 font-medium text-center">Attivo</th>
              </tr>
            </thead>
            <tbody>
              {canali.map((c) => (
                <tr key={c.id} className="border-t border-[#DDE5E2]" data-testid={`canale-row-${c.slug}`}>
                  <td className="px-4 py-3 font-medium">{c.nome}</td>
                  <td className="px-4 py-3 tnum">{c.slug}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => copy(c.slug)} className="flex items-center gap-2 text-[#4F708C] hover:underline">
                      <span className="truncate max-w-[220px]">{linkFor(c.slug)}</span>
                      {copied === c.slug ? <Check className="h-4 w-4 text-[#4F683E]" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch data-testid={`canale-toggle-${c.slug}`} checked={c.attivo} onCheckedChange={() => toggle(c)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold text-[#2B3A42] mb-3">Riepilogo canali</h2>
        <div className="bg-white rounded-2xl border border-[#DDE5E2] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="canali-riepilogo-table">
              <thead>
                <tr className="bg-[#EDF2F0] text-[#2B3A42] text-left">
                  <th className="px-4 py-3 font-medium">Canale</th>
                  <th className="px-4 py-3 font-medium text-right">Simulazioni</th>
                  <th className="px-4 py-3 font-medium text-right">Contattate</th>
                  <th className="px-4 py-3 font-medium text-right">Incarichi acquisiti</th>
                  <th className="px-4 py-3 font-medium text-right">Credito medio stimato</th>
                </tr>
              </thead>
              <tbody>
                {riepilogo.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5B6E78]">Nessun dato disponibile.</td></tr>
                ) : riepilogo.map((r) => (
                  <tr key={r.canale} className="border-t border-[#DDE5E2]" data-testid={`riepilogo-row-${r.canale}`}>
                    <td className="px-4 py-3 font-medium">{r.canale}</td>
                    <td className="px-4 py-3 text-right tnum">{r.n}</td>
                    <td className="px-4 py-3 text-right tnum">{r.contattata}</td>
                    <td className="px-4 py-3 text-right tnum">{r.incarico}</td>
                    <td className="px-4 py-3 text-right tnum">{fmtEuro(r.credito_medio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
